import React, { useRef, useState } from "react";
import { clp } from "@/lib/api";
import { Percent, X } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";

/** Monto en pesos que se descuenta según el tipo elegido (nunca llega al total). */
export const montoDescuentoManual = (desc, base) => {
  const v = Number(desc?.valor || 0);
  if (!v || v <= 0 || base <= 0) return 0;
  const m = desc.tipo === "PORCENTAJE" ? Math.round((base * Math.min(v, 99)) / 100) : Math.round(v);
  return Math.min(m, Math.max(0, base - 1));
};

/** Descuento manual del cajero en la pantalla de cobro: en pesos o porcentaje. */
export function DescuentoManual({ value, onChange, base, disabled, tope = 0, autorizadoPor, onAutorizar, onEnter }) {
  const monto = montoDescuentoManual(value, base);
  const inputRef = useRef(null);
  const pinRef = useRef(null);
  const [pin, setPin] = useState("");
  const excede = tope > 0 && base > 0 && (monto * 100) / base > tope + 0.5;
  const tipos = [{ k: "MONTO", t: "$ Pesos" }, { k: "PORCENTAJE", t: "% Porcentaje" }];
  return (
    <div data-testid="pos-pay-descuento" className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-2.5 space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-widest text-amber-800 flex items-center gap-1">
        <Percent className="w-3 h-3" /> Descuento manual
      </p>
      <div className="flex gap-1">
        {tipos.map((o) => (
          <button key={o.k} type="button" disabled={disabled} data-testid={`pos-pay-descuento-tipo-${o.k.toLowerCase()}`}
            onClick={() => { onChange({ tipo: o.k, valor: "" }); setTimeout(() => inputRef.current?.focus(), 0); }}
            className={`flex-1 h-8 rounded-lg text-xs font-bold transition-colors ${value.tipo === o.k ? "bg-amber-500 text-white" : "bg-white text-amber-800 border border-amber-200"}`}>
            {o.t}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        <input ref={inputRef} type="number" min={0} max={value.tipo === "PORCENTAJE" ? 99 : undefined} disabled={disabled}
          value={value.valor} onChange={(e) => onChange({ ...value, valor: e.target.value })}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault(); e.stopPropagation();
            if (excede && !autorizadoPor) pinRef.current?.focus();
            else onEnter?.();
          }}
          data-testid="pos-pay-descuento-valor"
          placeholder={value.tipo === "PORCENTAJE" ? "Ej: 10" : "Ej: 1000"}
          className="flex-1 min-w-0 h-9 rounded-lg border border-amber-200 bg-white px-2 text-right font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
        {Number(value.valor) > 0 && (
          <button type="button" onClick={() => onChange({ ...value, valor: "" })} data-testid="pos-pay-descuento-quitar"
            className="h-9 w-9 grid place-items-center rounded-lg bg-white border border-amber-200 text-amber-700 hover:bg-amber-100">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {monto > 0 && (
        <p data-testid="pos-pay-descuento-monto" className="text-xs font-bold text-amber-800 text-right">
          Descuento: -{clp(monto)}
        </p>
      )}
      {tope > 0 && <p className="text-[10px] text-amber-700 text-right">Tope de la caja: {tope}%</p>}
      {excede && !autorizadoPor && (
        <div className="space-y-1" data-testid="pos-pay-descuento-autorizar">
          <p className="text-[11px] font-bold text-rose-700">Supera el tope: ingresa el PIN del jefe</p>
          <div className="flex gap-1">
            <PasswordInput ref={pinRef} wrapperClassName="flex-1 min-w-0" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") onAutorizar(pin); }}
              data-testid="pos-pay-descuento-pin" placeholder="PIN"
              className="flex-1 min-w-0 h-9 rounded-lg border border-rose-200 bg-white px-2 text-center font-mono-num focus:outline-none focus:ring-2 focus:ring-rose-300" />
            <button type="button" onClick={() => onAutorizar(pin)} data-testid="pos-pay-descuento-pin-ok"
              className="h-9 px-3 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700">Autorizar</button>
          </div>
        </div>
      )}
      {excede && autorizadoPor && (
        <p className="text-[11px] font-bold text-emerald-700 text-right" data-testid="pos-pay-descuento-autorizado">Autorizado por {autorizadoPor}</p>
      )}
    </div>
  );
}
